<script>
    /*
    	ReferralQuestion.svelte

        This is the main "routing" component for Referral questions.
        Depending on the questionType a specific UI control is rendered.
        This makes use of the new custom UI components in /tcare-ui folder

        New questionType: OBJECT - a block of input fields to be rendered

        Each ReferralQuestion contains a CSS .label-area (left) and a .control-area: (right)

            What is your first name?        <input>
            What is your last name?         <input>
            What is your relationship?      <radio> <radio> <radio>
            Do you need assistance?         <check> <check> <check>


            export const CONTACT_INFO = {
                sectionId: "contact-info",
                sectionlabel: "Contact Information",
                nextSectionId: "service-specific",
                questions: [
                    {
                        id: "0000-0000",
                        questionType: SINGLE_CHOICE,
                        label: "Who are you filling out this referral for?",
                        options: [
                            {label: "Myself", value: "myself"},
                            {label: "Someone else", value: "someone else"}
                        ],
                        isRequired: true,
                        value: null
                    },
                    {
                        id: "0000-0001",
                        questionType: OBJECT,
                        label: null,
                        options: null,
                        objectType: USER_INFO,          // for the correct field layouts
                        isRequired: true,               // need a way to determine required fields
                        value: userObject
                    },
                    {
                        id: "0000-0002",
                        questionType: MULTI_CHOICE,
                        label: "Who should we share this referral with?",
                        options: [
                            {label: "Me", value: "me"},
                            {label: "This Service", value: "this service"},
                            {label: "Our Partners", value: "our partners"}
                        ],
                        isRequired: true,
                        value: null
                    }
                ]
            }

    */

    import { onMount } from 'svelte';

    // TCARE custom UI components
	import TCareInput from "../tcare-ui/TCareInput.svelte";
	import TCareTextArea from "../tcare-ui/TCareTextArea.svelte";
	import TCareRadioButtonGroup from "../tcare-ui/TCareRadioButtonGroup.svelte";
	import TCareCheckBoxGroup from "../tcare-ui/TCareCheckBoxGroup.svelte";
	import TCareNumber from "../tcare-ui/TCareNumber.svelte";
	import TCareDate from "../tcare-ui/TCareDate.svelte";
	import TCareSelect from "../tcare-ui/TCareSelect.svelte";
	import TCareComment from "../tcare-ui/TCareComment.svelte";
	import TCareRatingStars from "../tcare-ui/TCareRatingStars.svelte";
	import TCareBooleanCheckBox from "../tcare-ui/TCareBoolCheckBox.svelte";

    // custom components for rendering Requirements sections
	import TCareReferralContactInfoFields from "../tcare-ui/TCareReferralContactInfoFields.svelte";
	import TCareReferralMyselfInfoFields from "../tcare-ui/TCareReferralMyselfInfoFields.svelte";
    
    // SMUI components


    // question types - back end must match!
    import {
        SHORT_TEXT,
        LONG_TEXT,
        SINGLE_CHOICE,
        MULTI_CHOICE,
        NUMBER,
        DATE,
        COMMENT,
        SELECTION,
        BOOLEAN_CHECKBOX,
        RATING_STARS,
        OBJECT,
        CONTACT_INFO,
        MYSELF_INFO,        
    } from './question-types.js';

    // prop names - back end must match!
    import {
        VALUE,
        QUESTION_TYPE,
        IS_REQUIRED,
        OPTIONS,
        LABEL,
        CSS_CLASS,
        IS_READONLY,
        PLACEHOLDER
    } from './question-types.js';

    
	// --tcui-primary: #611C73;
	// --tcui-secondary: #FE4672;
    const STAR_COLOR = '#FE4672';           // TCareRatingStars need a starColor prop

    export let question;
    export let setValue;                    // this could go here if not for dependency questions

    onMount(() => {
    })


</script>

<style>
    .referral-question {
        min-height: 30px;
        margin: 10px 0px;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: space-between;
        font-size: 14px;
        color: var(--tcui-dark-purple);
        /* border: 1px solid red; */
    }

    .referral-question .label-area {
        width: 40%;
        /* border: 1px solid green; */
    }

    .referral-question .spacer {
        width: 1%;
        /* border: 1px solid black; */
    }

    .referral-question .control-area {
        width: 58%;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: flex-start;
        /* border: 1px solid blue; */
    }

    .referral-question .is-required {
        /* font-weight: 800; */
        color: var(--tcui-secondary);
    }

    
    @media screen and (max-width: 600px) {
        .referral-question .label-area { width: 100%; }
        .referral-question .control-area { width: 100%; }
    }

    @media screen and (min-width: 601px) and (max-width: 1300px) {
    }

    @media screen and (min-width: 1201px) {
    }

</style>


<!-- first render the question label and then the correct UI control -->
<!-- the custom UI controls are in the /tcare-ui folder -->

<!-- no spacer and UI control area to the right -->
{#if question.questionType === COMMENT}
    <div class="label-area">
        <TCareComment label={question[LABEL]} cssClass={question[CSS_CLASS]} />
    </div>
{/if}


<!-- wrap all other question types than COMMENT -->
{#if question.questionType !== COMMENT}
    <div class="referral-question">

        <!-- OBJECT and COMMENT questionTypes will not have a label and/or UI control -->
        {#if question[QUESTION_TYPE] !== OBJECT}
            <div class="label-area">
                {#if question[IS_REQUIRED]}
                    <span class="is-required">{question[LABEL]}&nbsp;*</span>
                {:else}
                    {question[LABEL]}
                {/if}
            </div>

            <div class="spacer"></div>

            <!-- render the UI controls here based on question type -->
            <!-- many are just wrappers around standard HTML controls -->
            <div class="control-area">

                {#if question[QUESTION_TYPE] === SHORT_TEXT}
                    <TCareInput source={question} prop={VALUE} {setValue} placeholder={question[PLACEHOLDER] || ''} />

                {:else if question[QUESTION_TYPE] === LONG_TEXT}
                    <TCareTextArea source={question} prop={VALUE} {setValue}  placeholder={question[PLACEHOLDER] || ''} />
                
                {:else if question[QUESTION_TYPE] === SINGLE_CHOICE}
                    <TCareRadioButtonGroup source={question} prop={VALUE} {setValue} 
                        options={question[OPTIONS]} 
                    />

                {:else if question[QUESTION_TYPE] === MULTI_CHOICE}
                    <TCareCheckBoxGroup source={question} prop={VALUE} {setValue} 
                        options={question[OPTIONS]} 
                    />
                
                {:else if question[QUESTION_TYPE] === NUMBER}
                    <TCareNumber source={question} prop={VALUE} {setValue} />
                
                {:else if question[QUESTION_TYPE] === DATE}
                    <!-- the date will be question.value -->
                    <TCareDate source={question} prop={VALUE} placeholder={question[PLACEHOLDER] || ''}/>
                            
                {:else if question[QUESTION_TYPE] === SELECTION}
                    <TCareSelect source={question} prop={VALUE} {setValue} 
                        options={question[OPTIONS]} value={''}
                    />

                {:else if question[QUESTION_TYPE] === BOOLEAN_CHECKBOX}
                    <TCareBooleanCheckBox source={question} prop={VALUE} {setValue} label={question[LABEL]} />
                
                {:else if question[QUESTION_TYPE] === RATING_STARS}
                    <TCareRatingStars 
                        source={question} 
                        prop={VALUE} 
                        {setValue} 
                        isReadOnly={question[IS_READONLY]}
                        starColor={STAR_COLOR}
                    />
                
                {/if}            
            </div>
        {/if}

        
        <!-- custom objects are supported - must have corresponding component -->
        {#if question.questionType === OBJECT}
            <!-- custom object #1 - set required fields on the object itself -->
            {#if question.objectType === CONTACT_INFO}
                <TCareReferralContactInfoFields source={question[VALUE]} {setValue} />
            {/if}

            <!-- custom object #2 -->
            {#if question.objectType === MYSELF_INFO}
                <TCareReferralMyselfInfoFields source={question[VALUE]} {setValue} />
            {/if}

            <!-- other custom object types can go here :) -->
        {/if}

    </div>
{/if}